import PropTypes from 'prop-types'
import React from 'react'
import ReactCSSTransitionGroup from 'react-addons-css-transition-group'
import classNames from 'classnames'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'

import { toRoman } from 'helpers'
import { canManageBuilding, canPrepareReserve } from 'helpers/accounts'
import {
    BUILDING_TITLES,
    RESERVE_TITLES,
    RESERVE_USAGE_BATTLE_TYPES,
} from 'helpers/i18n.jsx'

import requestSlotInfo from 'state/actions/slots'
import { playHoverSound, playClickSound } from 'state/actions/sounds'

import FirstChild from 'components/FirstChild/FirstChild.jsx'
import ModernizationOverlay from 'components/ModernizationOverlay/ModernizationOverlay.jsx'
import UpgradeEffect from 'components/UpgradeEffect/UpgradeEffect.jsx'
import LoadingScreen from 'components/LoadingScreen/LoadingScreen.jsx'
import NavigationArrow from 'components/NavigationArrow/NavigationArrow.jsx'

import {
    CancelConfirmation,
    CardImage,
    ReserveProduce,
    ReserveSelect,
    UpgradeConfirmation,
    UpgradeEffectContent,
    UpgradeTooltip,
} from './components'

import { BUILDING, STRONGHOLD } from 'urls'
import {
    COMMAND_CENTER_ID,
    KEY_CODES,
    MAX_BUILDING_LEVEL,
} from 'constants'
import {
    UPGRADE_TOOLTIP_ID,
} from './constants.jsx'

import styles from './BuildingCard.scss'

export class BuildingCard extends React.Component {
    static propTypes = {
        buildingId: PropTypes.number.isRequired,
        slotId: PropTypes.number.isRequired,
        loading: PropTypes.bool.isRequired,
        reserveProduceConfirmationOpened: PropTypes.bool.isRequired,
        hasPermission: PropTypes.bool.isRequired,
        hasPermissionPrepareReserve: PropTypes.bool.isRequired,
        building: PropTypes.object.isRequired,
        buildings: PropTypes.object.isRequired,
        buildingUpgrade: PropTypes.shape({
            confirmationOpened: PropTypes.bool.isRequired,
            submitting: PropTypes.bool.isRequired,
            highlighted: PropTypes.bool.isRequired,
        }).isRequired,
        reserveInfo: PropTypes.object.isRequired,

        playHoverSound: PropTypes.func.isRequired,
        playClickSound: PropTypes.func.isRequired,
        pushState: PropTypes.func.isRequired,
        requestSlotInfo: PropTypes.func.isRequired,
    }
    constructor(props) {
        super(props)
        this.state = {
            loading: true,
        }
        this.backHandler = props.pushState.bind(null, STRONGHOLD)
        this.onKeyUp = this.onKeyUp.bind(this)
    }
    onKeyUp(e) {
        if (e.keyCode !== KEY_CODES.ESCAPE) return

        const confirmationOpened = (
            this.props.buildingUpgrade.confirmationOpened ||
            (this.refs.overlay && this.refs.overlay.state.opened) ||
            this.props.reserveProduceConfirmationOpened
        )
        if (!confirmationOpened) {
            this.backHandler()
        }
    }
    componentWillReceiveProps(nextProps) {
        this.setState({ loading: nextProps.loading })
        if (this.props.slotId !== nextProps.slotId) {
            this.props.requestSlotInfo(this.props.slotId)
            this.setState({ loading: true })
        }
    }
    componentWillMount() {
        this.props.requestSlotInfo(this.props.slotId)
        window.addEventListener('keydown', this.onKeyUp)
    }
    componentWillUnmount() {
        window.removeEventListener('keydown', this.onKeyUp)
    }
    handleUpgradeEffectClick() {
        this.props.playClickSound()
        this.refs.overlay.show()
    }

    render() {
        if (this.state.loading) return (
            <div className={styles.base}>
                <LoadingScreen />
            </div>
        )

        const { building, buildings, buildingUpgrade, reserveInfo } = this.props

        const buildingLevel = (buildingUpgrade.highlighted ? building.level + 1 : building.level)

        const overlayClassName = classNames(styles.overlay, {
            [styles.overlay__enabled]: buildingUpgrade.highlighted,
        })

        const arrowLeftClassName = classNames(styles.arrow, styles.arrow__left)
        const arrowRightClassName = classNames(styles.arrow, styles.arrow__right)

        let handleClickPrev = null
        let handleClickNext = null
        let rightArrowDisabled = true

        const buildingsIds = Object.keys(buildings).map(buildingId => parseInt(buildingId))
        const buildingId = parseInt(building.id)
        const currentBuildingIdx = buildingsIds.indexOf(buildingId)
        if (currentBuildingIdx === 0) {
            handleClickPrev = this.props.pushState.bind(
                null, BUILDING(COMMAND_CENTER_ID)
            )
        } else {
            handleClickPrev = this.props.pushState.bind(
                null, BUILDING(buildingsIds[currentBuildingIdx - 1])
            )
        }
        if (currentBuildingIdx < (buildingsIds.length - 1)) {
            handleClickNext = this.props.pushState.bind(
                null, BUILDING(buildingsIds[currentBuildingIdx + 1])
            )
            rightArrowDisabled = false
        }

        return (
            <div className={styles.base}>
                <div className={overlayClassName} />
                <div className={styles.header}>
                    <h2 className={styles.title}>
                        <span className={styles.level}>
                            {toRoman(buildingLevel)}
                            <span className={styles.levelShadow}>{toRoman(buildingLevel)}</span>
                        </span>
                        {BUILDING_TITLES[building.type]}
                    </h2>
                </div>

                <div className={styles.main}>
                    <div className={styles.wrapper}>
                        <div className={styles.holder}>
                            <div className={styles.grid}>
                                <div className={classNames(styles.cell, styles.cell__centred)}>
                                    <CardImage
                                        tooltipId={`${UPGRADE_TOOLTIP_ID}${building.id}`}
                                        building={building}
                                        reserveType={reserveInfo.type}
                                    />
                                    <UpgradeEffect
                                        handleClick={this.handleUpgradeEffectClick.bind(this)}
                                        handleHover={this.props.playHoverSound}
                                        hasPermission={this.props.hasPermission}
                                        topLevel={buildingLevel === MAX_BUILDING_LEVEL}
                                        fade={buildingUpgrade.highlighted}
                                    />
                                </div>
                                <div className={styles.cell}>
                                    <div className={styles.reserve}>
                                        <h3 className={styles.heading}>{RESERVE_TITLES[reserveInfo.type]}</h3>
                                        {RESERVE_USAGE_BATTLE_TYPES[reserveInfo.type] && (
                                            <p className={styles.reserveDescription}>
                                                {RESERVE_USAGE_BATTLE_TYPES[reserveInfo.type]}
                                            </p>
                                        )}
                                        <ReserveSelect buildingId={this.props.buildingId} />
                                    </div>
                                    <div className={styles.produce}>
                                        <ReserveProduce
                                            reserve={reserveInfo}
                                            buildingId={building.id}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={arrowLeftClassName}>
                            <NavigationArrow
                                size={NavigationArrow.SIZES.big}
                                type={NavigationArrow.TYPES.left}
                                handleClick={handleClickPrev}
                            />
                        </div>
                        <div className={arrowRightClassName}>
                            <NavigationArrow
                                size={NavigationArrow.SIZES.big}
                                type={NavigationArrow.TYPES.right}
                                handleClick={handleClickNext}
                                disabled={rightArrowDisabled}
                            />
                        </div>
                    </div>
                </div>
                {this.props.hasPermission ? (
                    <div>
                        {buildingLevel < MAX_BUILDING_LEVEL ? (
                            <div>
                                <UpgradeTooltip
                                    id={`${UPGRADE_TOOLTIP_ID}${building.id}`}
                                    building={this.props.building}
                                    reserve={reserveInfo}
                                />
                            </div>
                        ) : null}

                        <ReactCSSTransitionGroup
                            component={FirstChild}
                            transitionName='upgradeConfirmation'
                            transitionEnterTimeout={200}
                            transitionLeaveTimeout={200}
                        >
                            {this.props.buildingUpgrade.confirmationOpened ? (
                                <UpgradeConfirmation building={this.props.building} />
                            ) : null}
                        </ReactCSSTransitionGroup>
                    </div>
                ) : null}
                {this.props.hasPermissionPrepareReserve &&
                    <CancelConfirmation buildingId={this.props.building.id} />
                }
                <ModernizationOverlay ref='overlay'>
                    <UpgradeEffectContent slotId={this.props.slotId}/>
                </ModernizationOverlay>
            </div>
        )
    }
}

export const mapStateToProps = (state, { buildingId }) => {
    const {
        account,
        buildings,
        buildingUpgrade,
        reserveProduce,
        reserveProduceCancel,
        slots,
        settings,
    } = state

    const building = buildings[buildingId]
    const slotId = Object.keys(slots).find(slotId => (slots[slotId]['building'] === buildingId))
    const slot = slots[slotId]
    const reserveType = settings.buildings[building.type].reserve_type
    const reserveInfo = settings.reserves[reserveType]

    return {
        building,
        buildings,
        buildingUpgrade,
        reserveInfo,
        slotId: parseInt(slotId),
        loading: (
            slot.loading === true ||
            reserveProduce.submitting === true ||
            reserveProduceCancel.submitting === true
        ),
        reserveProduceConfirmationOpened: reserveProduceCancel.confirmationOpened,
        hasPermission: canManageBuilding(account),
        hasPermissionPrepareReserve: canPrepareReserve(account),
    }
}

export const mapDispatchToProps = {
    playHoverSound,
    playClickSound,
    pushState: push,
    requestSlotInfo,
}

export default connect(mapStateToProps, mapDispatchToProps)(BuildingCard)



// WEBPACK FOOTER //
// ./containers/BuildingCard/BuildingCard.jsx